import {
  State,
  Action,
  StateContext,
  Selector,
  Store,
  Actions,
  ofActionDispatched,
} from '@ngxs/store';
import { BookmarksStateModel } from './bookmarks.state.model';
import {
  LoadBookmarks,
  LoadBookmarksSuccess,
  LoadOrganizationBookmarks,
  DeleteBookmark,
  DeleteBookmarkSuccess,
  SaveBookmark,
  SaveBookmarkSuccess,
  SelectBookmark,
  SelectBookmarkSuccess,
  SaveBookmarkFail,
  ResetBookmarks,
  SortBookmarks,
  CheckBookmark,
  SetCheckedBookmarks,
  ExportBookmarks,
  ExportBookmarksSuccess,
  ExportBookmarksError,
  LoadCollectionBookmarks,
  DeleteBookmarkFail,
  CopyBookmark,
  CopyBookmarkFailure,
  CopyBookmarkSuccess,
  SetBookmarkText,
  SetSelectedCategory,
  ClearBookmarks,
  UpdateBookmarkCollections,
  UpdateBookmarkCollectionsSuccess,
  UpdateBookmarkCollectionsError,
  RemoveBookmarkCollectionsSuccess,
  AddBookmarkCollectionsSuccess,
  RemoveBookmarkCollectionsError,
  AddBookmarkCollectionsError,
  CancelCopyBookmark,
  CancelSaveBookmark,
  SetBookmarkViewMode,
} from './bookmarks.actions';

import { finalize, tap, mergeMap, take, switchMap, map } from 'rxjs/operators';
import { BookmarksService } from '../bookmarks.service';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { forkJoin, from, Observable, of } from 'rxjs';
import { bookmarkSortRecent } from '../data/bookmark-sort';
import { PublishService } from 'src/app/publish/publish.service';
import { WidgetOptions } from 'src/app/publish/models/widget-options.model';
import { OrganizationsService } from 'src/app/organizations/organizations.service';
import {
  AddCollectionContentModel,
  CollectionContentModel,
  RemoveCollectionContentModel,
} from 'src/app/organizations/models/collection.content.model';
import { ExploreService } from 'src/app/explore/explore.service';
import { StoryModel } from 'src/app/story/models/story.model';
import { cloneDeep, invert } from 'lodash';
import { sortModels } from '../models/bookmark-sort.model';
import { ChapterUtils } from 'src/app/shared/chapter-utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ParticleActions } from 'src/app/engine/state/particles/particles.actions';
import { Injectable } from '@angular/core';
import { BookmarkEntryModel } from '../models/bookmark.entry.model';
import { StoryService } from 'src/app/story/story.service';
import { BookmarkModel } from '../models/bookmark.model';
import { PendoService } from 'src/app/shared/pendo.service';
import { StoryChapterThumbnail } from 'src/app/story/models/story-chapter-thumbnail.model';
import { StoryThumbnailsState } from 'src/app/story/state/thumbnails/story.thumbnails.state';
import { RegisterChapterThumbnails } from 'src/app/story/state/thumbnails/story.thumbnails.actions';
import { ImagesService } from 'src/app/images/images.service';
import { ChapterImage } from 'src/app/images/models/chapter-image.model';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { TweenParticleLookup } from 'src/app/shared/tween-particle-lookup';
import { BookmarkViewModeTag } from '../models/bookmark-view-mode.enum';
import { normalizeBookmarkModel } from '../lib/bookmark-utils';
import { DatabaseStorageEnum, StorageService } from 'src/app/shared/storage.service';
import { ContentType } from 'src/app/story/models/content-type.enum';

function getBookmarkModel(
  bookmarksService: BookmarksService,
  exploreService: ExploreService,
  storageService: StorageService,
  id: string,
  type: ContentType,
  preview: boolean,
): Observable<BookmarkModel> {
  if (preview) {
    // preview bookmark
    return from(storageService.getDatabaseItem(DatabaseStorageEnum.PreviewBookmark));
  } else if (type === ContentType.Bookmark) {
    // user bookmark
    return bookmarksService.getBookmarkModel(id);
  } else {
    // default: module
    return exploreService.getConditionModel(id);
  }
}

const BookmarksStateDefaults: BookmarksStateModel = {
  loading: true,
  exporting: false,
  copying: false,
  saving: false,
  bookmarks: [],
  selectedBookmark: null,
  sort: bookmarkSortRecent,
  checkedBookmarks: [],
  selectedCategory: null,
};

@State<BookmarksStateModel>({
  name: 'bookmarks',
  defaults: BookmarksStateDefaults,
})
@Injectable()
export class BookmarksState {
  @Selector()
  static getBookmarks(state: BookmarksStateModel) {
    const { bookmarks, sort } = state;
    const { ascending, fields } = sort;
    return sortModels(bookmarks, fields, ascending);
  }

  @Selector()
  static getCheckedBookmarks(state: BookmarksStateModel) {
    return state.checkedBookmarks;
  }

  @Selector()
  static getSelectedBookmark(state: BookmarksStateModel) {
    return state.selectedBookmark;
  }

  @Selector()
  static getSelectedBookmarkViewMode(state: BookmarksStateModel) {
    return state.selectedBookmark.viewMode || BookmarkViewModeTag.All;
  }

  @Selector()
  static getLoading(state: BookmarksStateModel) {
    return state.loading;
  }

  @Selector()
  static getExporting(state: BookmarksStateModel) {
    return state.exporting;
  }

  @Selector()
  static getSaving(state: BookmarksStateModel) {
    return state.saving;
  }

  @Selector()
  static getCopying(state: BookmarksStateModel) {
    return state.copying;
  }

  @Selector()
  static getSort(state: BookmarksStateModel) {
    return state.sort;
  }

  @Selector()
  static getSelectedCategory(state: BookmarksStateModel) {
    return state.selectedCategory;
  }

  @Action(LoadBookmarks)
  loadBookmarks(context: StateContext<BookmarksStateModel>, action: LoadBookmarks) {
    context.patchState({
      loading: true,
      bookmarks: [],
      checkedBookmarks: [],
    });

    // load private content (private bookmarks, custom modules)
    forkJoin(
      this.bookmarksService.getPrivateBookmarks(action.query),
      this.bookmarksService.getPrivateConditions(action.query),
    )
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(([bookmarks, conditions]) => {
        context.dispatch(new LoadBookmarksSuccess([...bookmarks, ...conditions]));
      });
  }

  @Action(LoadOrganizationBookmarks)
  loadOrganizationBookmarks(
    context: StateContext<BookmarksStateModel>,
    { organizationId }: LoadOrganizationBookmarks,
  ) {
    context.patchState({
      loading: true,
      bookmarks: [],
      checkedBookmarks: [],
    });

    // load group content (private bookmarks and custom modules)
    this.bookmarksService
      .getOrganizationContent(organizationId)
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe((bookmarks) => {
        context.dispatch(new LoadBookmarksSuccess(bookmarks));
      });
  }

  @Action(LoadCollectionBookmarks)
  loadCollectionBookmarks(
    context: StateContext<BookmarksStateModel>,
    action: LoadCollectionBookmarks,
  ) {
    context.patchState({
      loading: true,
      bookmarks: [],
      checkedBookmarks: [],
    });

    this.organizationsService
      .getCollectionContent(action.organizationId, action.collectionId)
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe((result) => {
        context.dispatch(new LoadBookmarksSuccess(result));
      });
  }

  @Action(LoadBookmarksSuccess)
  loadSuccess({ patchState }: StateContext<BookmarksStateModel>, action: LoadBookmarksSuccess) {
    patchState({
      bookmarks: action.result,
    });
  }

  @Action(SelectBookmark)
  selectBookmark(context: StateContext<BookmarksStateModel>, action: SelectBookmark) {
    const { id, type, preview } = action;

    const bookmark$ = getBookmarkModel(
      this.bookmarksService,
      this.exploreService,
      this.storageService,
      id,
      type,
      preview,
    );

    bookmark$.subscribe((bookmark) => {
      context.dispatch(new SelectBookmarkSuccess(bookmark));
    });
  }

  @Action(SelectBookmarkSuccess)
  selectBookmarkSuccess(context: StateContext<BookmarksStateModel>, action: SelectBookmarkSuccess) {
    let { bookmark } = action;

    if (bookmark) {
      bookmark = normalizeBookmarkModel(bookmark);
    }

    context.patchState({
      selectedBookmark: bookmark,
    });
  }

  // Replace existing or Save new bookmark
  @Action(SaveBookmark)
  async saveBookmark(context: StateContext<BookmarksStateModel>, action: SaveBookmark) {
    // begin save
    context.patchState({ saving: true });

    // requires story
    let serialized: { story: StoryModel };

    const bookmarkEntry = action.bookmarkEntry;
    const chapters = this.store.selectSnapshot(StoryChaptersState.getStoryChapters);
    const makeNewChapterIds = action.inplace === false;

    // 1. Make new chapter IDs
    const chapterIdMap = ChapterUtils.makeChapterIdMap(chapters, makeNewChapterIds);

    if (makeNewChapterIds) {
      await this.store
        .dispatch(new ParticleActions.UpdateTweenParticleLookupChapters(chapterIdMap))
        .toPromise();
    }

    if (action.includeIndex) {
      serialized = this.bookmarksService.createBookmarkStory(chapterIdMap); // Just a serialized module
      bookmarkEntry.index = serialized.story;
    }

    // If we're saving the whole bookmark AND thumbnails have been changed, save the thumbnails first
    // We do this so we can update the bookmark with the thumbnail URLs
    let thumbnailSave$: Observable<any> = of([]);
    if (action.includeIndex) {
      const thumbnails = this.store.selectSnapshot(StoryThumbnailsState.getRegisteredThumbnails);

      const requests$ = this.imagesService.saveChapterThumbnails(
        bookmarkEntry.index.chapters,
        thumbnails,
        invert(chapterIdMap),
      );

      if (requests$.length) {
        thumbnailSave$ = forkJoin(requests$);
      }
    }

    // 1. Save the thumbnails
    // 2. Map the returned thumbnail URLs to the bookmark
    // 3. Save the bookmark
    thumbnailSave$
      .pipe(
        mergeMap((results: ChapterImage[]) => {
          // Iterate through the new saved thumbnails and update their URL references in the chapters
          const thumbnails: Map<string, StoryChapterThumbnail> = this.store.selectSnapshot(
            StoryThumbnailsState.getRegisteredThumbnails,
          );
          results.forEach((thumbnail) => {
            const storyChapterThumbnail = new StoryChapterThumbnail();
            storyChapterThumbnail.storyId = bookmarkEntry.id;
            (storyChapterThumbnail.chapterId = thumbnail.chapterId),
              (storyChapterThumbnail.urls = {
                small: thumbnail.uris.small,
                large: thumbnail.uris.large,
              });
            thumbnails.set(thumbnail.chapterId, storyChapterThumbnail);

            const chapterIndex = bookmarkEntry.index.chapters.findIndex(
              (_chapter) => _chapter.id === thumbnail.chapterId,
            );
            if (chapterIndex >= 0) {
              bookmarkEntry.index.chapters[chapterIndex].thumbnails = { ...thumbnail.uris };
            }
          });

          context.dispatch(new RegisterChapterThumbnails(thumbnails));

          return this.bookmarksService.saveBookmark(bookmarkEntry);
        }),
      )
      .subscribe(
        (bookmark) => {
          context.patchState({ saving: false });

          const story: StoryModel = serialized ? serialized.story : null;
          const method = bookmarkEntry.id ? 'update' : 'save';

          const hasAddCollections =
            Array.isArray(bookmarkEntry.addCollections) && bookmarkEntry.addCollections.length > 0;

          const hasRemoveCollections =
            bookmarkEntry.removeCollections &&
            Array.from(bookmarkEntry.removeCollections.keys()).length > 0;

          // Add/remove collections
          if (hasAddCollections || hasRemoveCollections) {
            this.actions
              .pipe(
                ofActionDispatched(
                  UpdateBookmarkCollectionsSuccess,
                  UpdateBookmarkCollectionsError,
                ),
                take(1),
              )
              .subscribe(() => {
                context.dispatch(new SaveBookmarkSuccess(bookmark, story, method));
              });

            context.dispatch(
              new UpdateBookmarkCollections(
                bookmark,
                bookmarkEntry.addCollections,
                bookmarkEntry.removeCollections,
              ),
            );
          } else {
            context.dispatch(new SaveBookmarkSuccess(bookmark, story, method));
          }
        },
        () => {
          context.dispatch(new SaveBookmarkFail(bookmarkEntry));
        },
      );

    this.pendoService.track('Bookmark Save - Start');
  }

  @Action(SaveBookmarkSuccess)
  saveBookmarkSuccess(context: StateContext<BookmarksStateModel>, action: SaveBookmarkSuccess) {
    const savedBookmark = action.bookmark;
    const bookmarks = context.getState().bookmarks;

    // patch bookmark entry
    const savedBookmarkIndex = bookmarks.findIndex((bookmark) => bookmark.id === savedBookmark.id);
    if (savedBookmarkIndex !== -1) {
      bookmarks[savedBookmarkIndex] = savedBookmark;
      context.patchState({
        bookmarks: [...bookmarks],
      });
    }

    // update selected bookmar if replacing (save)
    if (action.method === 'save') {
      context.patchState({
        selectedBookmark: action.bookmark,
      });
    }

    this.pendoService.track('Bookmark Save - Success');
  }

  @Action(SaveBookmarkFail)
  saveBookmarkFail(context: StateContext<BookmarksStateModel>, action: SaveBookmarkFail) {
    this.pendoService.track('Bookmark Save - Failure');
  }

  @Action(DeleteBookmark)
  deleteBookmark(context: StateContext<BookmarksStateModel>, action: DeleteBookmark) {
    const bookmarkId = action.bookmarkId;

    this.bookmarksService.deleteBookmark(bookmarkId).subscribe(
      () => {
        context.dispatch(new DeleteBookmarkSuccess(bookmarkId));
      },
      (message) => {
        context.dispatch(new DeleteBookmarkFail(bookmarkId, message));
      },
    );
  }

  @Action(DeleteBookmarkSuccess)
  deleteBookmarkSuccess(context: StateContext<BookmarksStateModel>, action: DeleteBookmarkSuccess) {
    const bookmarks = context
      .getState()
      .bookmarks.filter((bookmark) => bookmark.id !== action.bookmarkId);

    context.patchState({
      bookmarks: [...bookmarks],
    });

    this.snackbar.open('Successfully deleted 3D model');
  }

  @Action(SortBookmarks)
  sortBookmarks(context: StateContext<BookmarksStateModel>, action: SortBookmarks) {
    const bookmarks = context.getState().bookmarks;

    context.patchState({
      sort: action.sort,
      bookmarks: [...bookmarks],
    });
  }

  @Action(ClearBookmarks)
  clearBookmarks(context: StateContext<BookmarksStateModel>, action: ClearBookmarks) {
    context.patchState({
      bookmarks: [],
      checkedBookmarks: [],
    });
  }

  @Action(ResetBookmarks)
  resetBookmarks(context: StateContext<BookmarksStateModel>, action: ResetBookmarks) {
    context.setState(BookmarksStateDefaults);
  }

  @Action(CheckBookmark)
  checkBookmark(context: StateContext<BookmarksStateModel>, action: CheckBookmark) {
    let checkedBookmarks = context.getState().checkedBookmarks;
    const exists = checkedBookmarks.some((bookmark) => bookmark === action.id);

    if (action.checked && !exists) {
      checkedBookmarks = [...checkedBookmarks, action.id];
    } else if (!action.checked) {
      checkedBookmarks = checkedBookmarks.filter((bookmark) => bookmark !== action.id);
    }

    context.patchState({
      checkedBookmarks: checkedBookmarks,
    });
  }

  @Action(SetCheckedBookmarks)
  setCheckedBookmarks(context: StateContext<BookmarksStateModel>, action: SetCheckedBookmarks) {
    context.patchState({
      checkedBookmarks: action.ids,
    });
  }

  @Action(ExportBookmarks)
  exportBookmarks(context: StateContext<BookmarksStateModel>, action: ExportBookmarks) {
    const {
      bookmarks,
      organizationId,
      collections,
      widgetURL,
      widgetOptions,
      offlineEnabled,
      productAccountId,
    } = action;
    const exportWidgetOptions = widgetOptions || new WidgetOptions();

    context.patchState({ exporting: true });

    this.publishService
      .exportBookmarks(
        bookmarks,
        organizationId,
        collections,
        widgetURL,
        exportWidgetOptions,
        null,
        action.userLanguage,
        action.userApp,
        offlineEnabled,
        productAccountId,
      )
      .pipe(
        finalize(() => {
          context.patchState({ exporting: false });
        }),
      )
      .subscribe((success) => {
        if (success) {
          context.dispatch(new ExportBookmarksSuccess(bookmarks));
        } else {
          context.dispatch(new ExportBookmarksError(bookmarks));
        }
      });
  }

  @Action(UpdateBookmarkCollections)
  updateBookmarkCollections(
    context: StateContext<BookmarksStateModel>,
    action: UpdateBookmarkCollections,
  ) {
    const addCollections = action.addCollections;

    const addContent: AddCollectionContentModel[] = addCollections.map((collection) => {
      const collectionContent: CollectionContentModel = {
        content_type: action.bookmark.type,
        content_id: action.bookmark.id,
      };

      return {
        collection_id: collection.collection_id,
        content_list: [collectionContent],
      };
    });

    const removeCollections = Array.from(action.removeCollections.keys());

    const removeContent: RemoveCollectionContentModel[] = removeCollections.map((collection) => {
      return {
        collection_id: collection.collection_id,
        collection_content_id_list: [action.removeCollections.get(collection)],
      };
    });

    const groupId = addCollections.concat(removeCollections)[0].group_id;

    this.organizationsService.updateCollectionContent(groupId, addContent, removeContent).subscribe(
      () => {
        if (addCollections.length > 0) {
          context.dispatch(new AddBookmarkCollectionsSuccess(action.bookmark, addCollections));
        }

        if (removeCollections.length > 0) {
          context.dispatch(
            new RemoveBookmarkCollectionsSuccess(action.bookmark, removeCollections),
          );
        }

        context.dispatch(
          new UpdateBookmarkCollectionsSuccess(action.bookmark, addCollections, removeCollections),
        );
      },
      () => {
        if (addCollections.length > 0) {
          context.dispatch(new AddBookmarkCollectionsError(action.bookmark, addCollections));
        }

        if (removeCollections.length > 0) {
          context.dispatch(new RemoveBookmarkCollectionsError(action.bookmark, removeCollections));
        }

        context.dispatch(
          new UpdateBookmarkCollectionsError(action.bookmark, addCollections, removeCollections),
        );
      },
    );
  }

  @Action(RemoveBookmarkCollectionsSuccess)
  removeBookmarkCollectionSuccess(
    context: StateContext<BookmarksStateModel>,
    action: RemoveBookmarkCollectionsSuccess,
  ) {
    const { bookmarks, selectedCategory } = context.getState();

    // if current state bookmarks are from collection data: update list
    if (selectedCategory === 'collection') {
      const bookmarkId = action.bookmark.id;
      const patchBookmarks = bookmarks.filter((bookmark) => bookmark.id !== bookmarkId);

      context.patchState({
        bookmarks: [...patchBookmarks],
      });
    }
  }

  @Action(CopyBookmark)
  copyBookmark(context: StateContext<BookmarksStateModel>, action: CopyBookmark) {
    this.pendoService.track('Bookmark Copy - Start');

    const { groupAccess, collections } = action;
    let newBookmark: BookmarkModel;

    context.patchState({ copying: true, saving: true });

    let oldStory: StoryModel;
    let newStory: StoryModel;
    let chapterIdMap: Record<string, string>;
    let viewMode: BookmarkViewModeTag;

    const sourceBookmark$ = getBookmarkModel(
      this.bookmarksService,
      this.exploreService,
      this.storageService,
      action.id,
      action.type,
      false,
    );

    const sourceStory$ = this.storyService.getStory(action.id, action.type, action.lang);

    forkJoin([sourceBookmark$, sourceStory$])
      .pipe(
        // Iterate over returned story and update any chapter ID references
        map(([sourceBookmark, sourceStory]) => {
          // Generate new chapter IDs
          oldStory = cloneDeep(sourceStory); // Keep around for thumbnail copying
          newStory = cloneDeep(sourceStory); // Rework into story that will be saved

          // Update chapter IDs in story
          chapterIdMap = ChapterUtils.makeChapterIdMap(oldStory.chapters, true);
          newStory.chapters.forEach((chapter: ChapterModel) => {
            chapter.id = chapterIdMap[chapter.id];
          });

          // Generate new particle ID lookup from new chapter ID map
          if (newStory.tweenParticleLookup) {
            newStory.tweenParticleLookup = TweenParticleLookup.updateChapterIds(
              newStory.tweenParticleLookup,
              chapterIdMap,
            );
          }

          // get source view mode
          viewMode = sourceBookmark.viewMode;
        }),

        // Generate fresh image blobs from old story remote images
        switchMap(() => {
          return this.imagesService.generateFreshChapterImages(oldStory.chapters, action.id);
        }),

        // Save new image blobs to AWS
        switchMap((chapterImages) => {
          const requests: Observable<ChapterImage>[] = chapterImages.map((image) => {
            return this.imagesService.saveNewChapterImage(image.chapterId, image.snapshot);
          });

          return requests.length ? forkJoin(requests) : of([]);
        }),

        // Map returned image URLs to new story chapters
        map((chapterImages) => {
          // Map image blobs to newly generated chapter IDs
          chapterImages.forEach((chapterImage) => {
            chapterImage.chapterId = chapterIdMap[chapterImage.chapterId];
          });

          newStory.chapters.forEach((chapter) => {
            const thumbnails = chapterImages.find(
              (chapterImage) => chapterImage.chapterId === chapter.id,
            );
            if (thumbnails) {
              chapter.thumbnails = { ...thumbnails.uris };
            }
          });
        }),

        // Rename the new bookmark and apply custom properties from the save dialog
        switchMap(() => {
          const entry = new BookmarkEntryModel();
          entry.title = action.title;
          entry.isOwner = true;
          // entry.id = copyId;
          entry.index = newStory;

          // set target view mode
          entry.viewMode = viewMode;

          // indicate copy
          entry.is_content_copy = true;

          if (groupAccess) {
            entry.add_group_access = [groupAccess];
          }

          return this.bookmarksService.saveBookmark(entry);
        }),

        tap((bookmark) => {
          newBookmark = bookmark;
        }),

        // Optionally add bookmark to collection
        switchMap(() => {
          if (groupAccess && collections && collections.length > 0) {
            const groupId = collections[0].group_id;

            const addContent = collections.map((collection) => {
              return {
                collection_id: collection.collection_id,
                content_list: [
                  {
                    content_type: newBookmark.type,
                    content_id: newBookmark.id,
                  } as CollectionContentModel,
                ],
              } as AddCollectionContentModel;
            });

            return this.organizationsService.updateCollectionContent(groupId, addContent, []);
          } else {
            return of(true);
          }
        }),

        finalize(() => {
          context.patchState({ copying: false, saving: false });
        }),
      )
      .subscribe(
        () => {
          this.pendoService.track('Bookmark Copy - Success');

          context.dispatch(new CopyBookmarkSuccess(newBookmark));
        },
        (error) => {
          this.pendoService.track('Bookmark Copy - Failure');

          context.dispatch(new CopyBookmarkFailure(action.id, error));
        },
      );
  }

  @Action(SetBookmarkText)
  setBookmarkText(context: StateContext<BookmarksStateModel>, action: SetBookmarkText) {
    const selectedBookmark = context.getState().selectedBookmark;

    context.patchState({
      selectedBookmark: {
        ...selectedBookmark,
        title: action.title,
      },
    });
  }

  @Action(SetBookmarkViewMode)
  setBookmarkViewMode(context: StateContext<BookmarksStateModel>, action: SetBookmarkViewMode) {
    const selectedBookmark = context.getState().selectedBookmark;

    context.patchState({
      selectedBookmark: {
        ...selectedBookmark,
        viewMode: action.viewMode,
      },
    });
  }

  @Action(SetSelectedCategory)
  setSelectedCategory(
    context: StateContext<BookmarksStateModel>,
    { category }: SetSelectedCategory,
  ) {
    context.patchState({
      selectedCategory: category ? category : null,
    });
  }

  @Action(CancelSaveBookmark)
  cancelSaveBookmark(context: StateContext<BookmarksStateModel>, action: CancelSaveBookmark) {
    // TODO: doesn't actually block saving
    context.patchState({
      saving: false,
    });
  }

  @Action(CancelCopyBookmark)
  cancelCopyBookmark(context: StateContext<BookmarksStateModel>, action: CancelCopyBookmark) {
    // TODO: doesn't actually block copying
    context.patchState({
      copying: false,
    });
  }

  constructor(
    private store: Store,
    private actions: Actions,
    private bookmarksService: BookmarksService,
    private imagesService: ImagesService,
    private storyService: StoryService,
    private exploreService: ExploreService,
    private publishService: PublishService,
    private organizationsService: OrganizationsService,
    private snackbar: MatSnackBar,
    private pendoService: PendoService,
    private storageService: StorageService,
  ) {}
}
